/**
 * @fileOverview The project costs walkthrough — the running post-calculation: the portfolio over
 * every project, one project on its own, and the itemised cost statement of a single report. Pure
 * data. See docs/view-help.md and docs/view-help/projectcosts.md.
 */

import { PERMISSIONS } from "@/services/permission"
import { stepsOn } from "@/services/viewHelp/define"
import type { ViewHelpTour } from "@/services/viewHelp/types"

/**
 * Every step of the feature reuses the drawer entry's own gate (`nav-costs`), which is the pair of
 * cost permissions the four routes are guarded by. One place decides who sees any of this.
 */
const COSTS = "nav-costs"

/**
 * The cost/revenue switch only exists for somebody who may see at least one selling stream —
 * without any of these the page stays on the cost side and the toggle is not rendered at all.
 */
const SEE_SELLING = [
  PERMISSIONS.VIEW_ASSIGNED_PROJECT_MACHINE_SELLINGPRICE,
  PERMISSIONS.VIEW_FOREIGN_PROJECT_MACHINE_SELLINGPRICE,
  PERMISSIONS.VIEW_ASSIGNED_PROJECT_MATERIAL_SELLINGPRICE,
  PERMISSIONS.VIEW_FOREIGN_PROJECT_MATERIAL_SELLINGPRICE,
  PERMISSIONS.VIEW_ASSIGNED_PROJECT_ADDITIONAL_EFFORT_SELLINGPRICE,
  PERMISSIONS.VIEW_FOREIGN_PROJECT_ADDITIONAL_EFFORT_SELLINGPRICE,
  PERMISSIONS.VIEW_ASSIGNED_PROJECT_USERPOSITION_HOURLYPAY,
  PERMISSIONS.VIEW_FOREIGN_PROJECT_USERPOSITION_HOURLYPAY,
]

/** The sample project every single-project view of this tour shows, and its two sample reports. */
const PROJECT = { projectId: "p-nord" }
const SAMPLE_HWR = { projectId: "p-nord", hwrId: "hwr-nord-1" }
const SAMPLE_CR = { projectId: "p-nord", crId: "cr-nord-1" }

const onPortfolio = stepsOn("ProjectCosts", { navEntry: COSTS })
const onProject = stepsOn("ProjectCostsForOne", { navEntry: COSTS, at: { params: PROJECT } })
const onHwr = stepsOn("HourlyWageReportCosts", { navEntry: COSTS, at: { params: SAMPLE_HWR } })
const onCr = stepsOn("ConstructionReportCosts", { navEntry: COSTS, at: { params: SAMPLE_CR } })

// The related views: where the figures of this feature come from. Each one is reached by its way
// (`services/viewHelp/ways.ts`) and its `at` is the fallback for a reload.
const onPosition = stepsOn("UserPositionDetail", {
  navEntry: COSTS,
  permissions: [PERMISSIONS.VIEW_USER_POSITION_GENERAL],
  at: { params: { userPositionId: "pos-polier" } },
})
const onMaterial = stepsOn("MaterialDetail", {
  navEntry: COSTS,
  permissions: [PERMISSIONS.VIEW_MATERIAL_GENERAL],
  at: { params: { materialId: "mat-beton" } },
})
// The settings page itself is open to everybody signed in, so the step carries no gate of its own.
const onSettings = stepsOn("SettingsOverview", { navEntry: COSTS, at: { hash: "general" } })
const onEmployee = stepsOn("EmployeeDetail", {
  navEntry: COSTS,
  // The tab the field sits on needs one of the Unternehmen permissions; this is the one the wage
  // block around it is gated by.
  permissions: [PERMISSIONS.VIEW_USER_COMPANY_PAY],
  at: { params: { employeeId: "demo-1" }, hash: "company" },
})
// The project's two report registers, where every report's Kostenaufstellung is one row action away —
// the other door into the two report pages. Each step also needs the register's own right.
const onProjectReports = stepsOn("ProjectDetail", { navEntry: COSTS, at: { params: PROJECT } })

/**
 * The running post-calculation, in the order somebody asks about it: how the company as a whole
 * stands, then one site, then the single report a figure came from, and finally the settings every
 * amount on those pages is computed from, and the project's report registers that open a statement.
 *
 * The scenes work the page's own controls — the valuation, the quick ranges, the four views, the
 * report overlay, one day's popover and one week's reports. None of them presses a control that
 * writes: the budget, the Nachkalkulation and the manual entries are pointed at and explained, and
 * so is the print button, which would otherwise hand the reader a PDF of sample data.
 */
export const PROJECT_COSTS_TOUR: ViewHelpTour = {
  key: "projectcosts",
  icon: "mdi-cash-clock",
  // The portfolio first, then one project, then the two report statements a day's cost is made of.
  routes: ["ProjectCosts", "ProjectCostsForOne", "HourlyWageReportCosts", "ConstructionReportCosts"],
  // Where the figures come from: the two prices and the two employer's-contribution fields — plus the
  // project's report registers, which open the two report statements from outside the feature.
  related: ["UserPositionDetail", "MaterialDetail", "SettingsOverview", "EmployeeDetail", "ProjectDetail"],
  // The ways that only make sense inside this tour, each from the page it starts on; every other view,
  // and these from anywhere else, is reached by its shared way (`services/viewHelp/ways.ts`) or loaded.
  // Played in order, all three hops between the own views are walked with the pointer, so the reader
  // sees how each page is reached.
  ways: {
    // One project's cost page has a single entry point, its project's Aktionen menu. From the portfolio
    // the project is one click away — its name in the heatmap row links to it — which is shorter than
    // the shared way through the project list.
    ProjectCostsForOne: {
      from: {
        ProjectCosts: [
          { do: "click", target: "costs-heatmap-project-p-nord" },
          { do: "scroll", target: "project-tabs", to: "reveal" },
          { do: "click", target: "project-actions" },
          { do: "wait", ms: 400 },
          { do: "click", target: "project-action-costs" },
        ],
      },
    },
    // A report's cost statement is opened from the week view: the week is expanded, and its first
    // report opens its own page — the same click the heatmap's day popover offers.
    HourlyWageReportCosts: {
      from: {
        ProjectCostsForOne: [
          { do: "check", target: "costs-mode-week" },
          { do: "expand", target: "costs-week-row" },
          { do: "click", target: "costs-week-entry" },
        ],
      },
    },
    // A construction report's statement from the other entry point, its project's register: the
    // project named on the hourly-wage statement, its Bautagesberichte, and the Kostenaufstellung in the
    // row menu of `cr-nord-1` (the register is narrow enough in the frame to fold its actions into one).
    // Not Zurück and the week view: Zurück goes back in the frame's history, which a frame loaded
    // straight onto the statement does not have. The pause is for the menu, as on the project's
    // Aktionen: its items are in the document before the overlay has faded in.
    ConstructionReportCosts: {
      from: {
        HourlyWageReportCosts: [
          { do: "click", target: "costs-project-link" },
          { do: "openTab", target: "project-tab-constructionReports" },
          { do: "click", target: "project-cr-actions-cr-nord-1" },
          { do: "wait", ms: 400 },
          { do: "click", target: "project-cr-actions-cr-nord-1-costs" },
        ],
      },
    },
  },
  scenes: [
    // The revenue side, and back to the cost side the rest of the tour talks about.
    {
      key: "valuation",
      enter: [{ do: "check", target: "costs-valuation-selling" }],
      exit: [{ do: "check", target: "costs-valuation-cost" }],
    },
    // A wider window, then the 30 days the page opens on.
    {
      key: "preset",
      enter: [{ do: "click", target: "costs-preset-90d" }],
      exit: [{ do: "click", target: "costs-preset-30d" }],
    },
    // The whole-company panel, opened and closed again: open, it pushes the heatmap off the page.
    {
      key: "combined",
      enter: [{ do: "expand", target: "costs-portfolio-head" }],
      exit: [{ do: "collapse", target: "costs-portfolio-head" }],
    },
    // One day's popover. Escape rather than a second click on the cell: the menu closes on a click
    // outside it, which the cell under it is.
    {
      key: "day",
      enter: [
        { do: "click", target: "costs-heatmap-cell" },
        // And the list of the reports behind the day, which the popover keeps folded away.
        { do: "click", target: "costs-heatmap-popover-reports" },
      ],
      exit: [{ do: "escape" }],
    },
    // The construction-report overlay, switched on and off again — it changes every total in view.
    {
      key: "show-cr",
      enter: [{ do: "check", target: "costs-show-cr" }],
      exit: [{ do: "uncheck", target: "costs-show-cr" }],
    },
    // The other three views of the same figures, left to right as the toggle has them: Verlauf, KW,
    // Analyse — and back to the heatmap for the footnote. One scene, so each step starts on the view the
    // step before it left open and the pointer goes straight on to the next button. Every press is a
    // `check`, made only when that view is not already the open one. The exit puts the heatmap back for
    // a reader who leaves the scene before its last step pressed it; after that step it presses nothing.
    { key: "modes", enter: [], exit: [{ do: "check", target: "costs-mode-heatmap" }] },
    // The week view with one week expanded, which is where a report's own statement is opened from.
    // Its exit puts the page back on the heatmap, so the way to the report page can click the same
    // three things again from a known state.
    {
      key: "week-reports",
      enter: [
        { do: "check", target: "costs-mode-week" },
        { do: "expand", target: "costs-week-row" },
      ],
      exit: [
        { do: "collapse", target: "costs-week-row" },
        { do: "check", target: "costs-mode-heatmap" },
      ],
    },
    // Max's page: the pointer presses its "Unternehmens-relevant" tab, and the field sits further
    // down that tab. Nothing to undo — leaving the page takes the tab with it.
    { key: "employee-company", enter: [], exit: [] },
    // A report register of the project with one row's action menu open, to show its Kostenaufstellung.
    // Nothing in the menu is pressed; Escape closes it, and the open tab needs no undoing.
    { key: "project-hwr-costs", enter: [], exit: [{ do: "escape" }] },
    { key: "project-cr-costs", enter: [], exit: [{ do: "escape" }] },
  ],
  steps: [
    // ── Die Übersicht über alle Projekte ─────────────────────────────────────
    // What the page is for, before any of its controls: the heading with its title and subtitle.
    onPortfolio("intro", "costs-head", "mdi-cash-clock", { placement: "bottom", zoom: false }),
    onPortfolio("projects", "costs-projects", "mdi-briefcase-outline", { placement: "bottom" }),
    onPortfolio("range", "costs-range", "mdi-calendar-range", { placement: "bottom" }),
    // No zoom: the quick range changes every figure on the page, which only shows with all of it in view.
    onPortfolio("presets", "costs-presets", "mdi-calendar-arrow-left", {
      placement: "top",
      scene: "preset",
      zoom: false,
    }),
    // Same reason: the switch turns every column of the page from cost to revenue.
    onPortfolio("valuation", "costs-valuation", "mdi-cash-plus", {
      placement: "bottom",
      scene: "valuation",
      zoom: false,
      permissions: SEE_SELLING,
    }),
    onPortfolio("modes", "costs-modes", "mdi-tab", { placement: "bottom" }),

    onPortfolio("health", "costs-portfolio", "mdi-heart-pulse", { placement: "bottom" }),
    onPortfolio("combined", "costs-portfolio", "mdi-sigma", { placement: "bottom", scene: "combined" }),

    onPortfolio("heatmap", "costs-heatmap", "mdi-grid", { placement: "top" }),
    onPortfolio("heatmap-row", "costs-heatmap-row", "mdi-table-row", { placement: "bottom" }),
    onPortfolio("heatmap-total", "costs-heatmap-total", "mdi-sigma", { placement: "left" }),
    // The popover is shown whole, without a zoom of its own: somebody who has just watched it open
    // wants to see what is in it.
    onPortfolio("day", "costs-heatmap-popover", "mdi-calendar-today", { placement: "right", scene: "day" }),
    // Both switches as one region, and no zoom: switching the overlay on changes the cells and
    // every total beside them, which only shows with the page in view.
    onPortfolio("show-cr", ["costs-show-hwr", "costs-show-cr"], "mdi-account-hard-hat", {
      placement: "bottom",
      scene: "show-cr",
      zoom: false,
    }),
    // The first project's chart, not the list of them, which is taller than the frame.
    onPortfolio("chart", "costs-chart", "mdi-chart-line", {
      placement: "top",
      scene: "modes",
      actions: [{ do: "check", target: "costs-mode-chart" }],
    }),
    // The first project's table, for the same reason.
    onPortfolio("week", "costs-week-table", "mdi-calendar-week", {
      placement: "top",
      scene: "modes",
      actions: [{ do: "check", target: "costs-mode-week" }],
    }),
    // The dot goes on the first card rather than on the whole view, which is taller than the frame.
    onPortfolio("analysis", "costs-analysis-structure", "mdi-chart-donut", {
      placement: "top",
      scene: "modes",
      actions: [{ do: "check", target: "costs-mode-analysis" }],
    }),
    // The footnote sits under the heatmap, which the pointer switches back to in front of the reader.
    onPortfolio("notice", "costs-notice", "mdi-information-outline", {
      placement: "top",
      scene: "modes",
      actions: [{ do: "check", target: "costs-mode-heatmap" }],
    }),

    // ── Ein einzelnes Projekt ────────────────────────────────────────────────
    onProject("single", "costs-single-project", "mdi-briefcase", { placement: "bottom" }),
    onProject("budget", "costs-budget", "mdi-wallet-outline", { placement: "bottom" }),
    onProject("plan", "costs-plan", "mdi-calculator-variant-outline", { placement: "bottom" }),
    onProject("manual", "costs-manual", "mdi-playlist-plus", { placement: "top" }),
    onProject("services", "costs-services", "mdi-format-list-numbered", { placement: "top" }),
    // The last step of this view, and the way into the next one: the same click the step demonstrates.
    onProject("week-reports", "costs-week-entry", "mdi-file-chart-outline", {
      placement: "top",
      scene: "week-reports",
    }),

    // ── Die Kostenaufstellung eines Stundenlohnberichts ──────────────────────
    onHwr("hwr-head", "costs-hwr-head", "mdi-account-clock-outline", { placement: "bottom" }),
    onHwr("hwr-summary", "costs-summary", "mdi-counter", { placement: "bottom" }),
    onHwr("hwr-labor", "costs-labor", "mdi-account-hard-hat", { placement: "top" }),
    onHwr("hwr-machines", "costs-hwr-machines", "mdi-bulldozer", { placement: "top" }),
    onHwr("hwr-materials", ["costs-hwr-materials", "costs-hwr-efforts"], "mdi-screw-machine-flat-top", {
      placement: "top",
    }),
    // Pointed at, never pressed: it would hand the reader a PDF of sample figures.
    onHwr("hwr-print", "costs-print", "mdi-printer", { placement: "bottom" }),
    // Pointed at, never pressed: the panel would cover the page the next steps are on. The button is
    // there only for somebody who may read the report, beyond the cost gate of the page.
    onHwr("hwr-open", "costs-open-report", "mdi-clipboard-text-clock-outline", {
      placement: "bottom",
      permissions: [
        PERMISSIONS.VIEW_ASSIGNED_PROJECT_HOURLYWAGEREPORT,
        PERMISSIONS.VIEW_FOREIGN_PROJECT_HOURLYWAGEREPORT,
      ],
    }),

    // ── Die Kostenaufstellung eines Bautagesberichts ─────────────────────────
    onCr("cr-summary", "costs-summary", "mdi-counter", { placement: "bottom" }),
    onCr("cr-shift", "costs-cr-shift", "mdi-clock-time-four-outline", { placement: "top" }),
    onCr("cr-labor", "costs-labor", "mdi-account-hard-hat", { placement: "top" }),
    onCr("cr-machines", "costs-cr-machines", "mdi-bulldozer", { placement: "top" }),
    // Where to find this again — on the last view of the feature, pointing at the button that opened it.
    onCr("again", "view-help-trigger", "mdi-help-circle-outline", { placement: "bottom" }),

    // ── Woher die Zahlen kommen ──────────────────────────────────────────────
    //
    // Four settings and one choice, each named together with the place in the pages above where it
    // takes effect. Nothing here is a tour of another feature's form: the rate, the purchase price,
    // the two surcharge fields and the source of the hours are exactly the inputs of the arithmetic
    // the reader has just watched.
    onPosition("position-rate", "position-rate", "mdi-account-cash-outline", { placement: "bottom" }),
    onMaterial("material-price", "material-purchase", "mdi-tag-outline", { placement: "bottom" }),
    onSettings("settings-contribution", "settings-contribution", "mdi-percent-outline", { placement: "bottom" }),
    onEmployee("employee-tabs", "employee-tabs", "mdi-tab", {
      placement: "bottom",
      scene: "employee-company",
      actions: [{ do: "openTab", target: "employee-tab-company" }],
    }),
    onEmployee("employee-contribution", "employee-contribution", "mdi-percent-outline", {
      placement: "top",
      scene: "employee-company",
    }),
    // The Kostenaufstellung in a row of each register. In the frame the registers are narrow enough to
    // fold their row actions into a menu, so the pointer opens it; on a wide screen it is an icon.
    onProjectReports("hwr-register-costs", "project-hwr-actions-hwr-nord-1-costs", "mdi-cash-multiple", {
      placement: "left",
      scene: "project-hwr-costs",
      at: { params: PROJECT, hash: "hourlyWageReports" },
      permissions: [
        PERMISSIONS.VIEW_ASSIGNED_PROJECT_HOURLYWAGEREPORT,
        PERMISSIONS.VIEW_FOREIGN_PROJECT_HOURLYWAGEREPORT,
      ],
      actions: [
        { do: "openTab", target: "project-tab-hourlyWageReports" },
        { do: "click", target: "project-hwr-actions-hwr-nord-1" },
      ],
    }),
    onProjectReports("cr-register-costs", "project-cr-actions-cr-nord-1-costs", "mdi-cash-multiple", {
      placement: "left",
      scene: "project-cr-costs",
      at: { params: PROJECT, hash: "constructionReports" },
      permissions: [
        PERMISSIONS.VIEW_ASSIGNED_PROJECT_CONSTRUCTIONREPORT,
        PERMISSIONS.VIEW_FOREIGN_PROJECT_CONSTRUCTIONREPORT,
      ],
      actions: [
        { do: "openTab", target: "project-tab-constructionReports" },
        { do: "click", target: "project-cr-actions-cr-nord-1" },
      ],
    }),
  ],
}
